/**
 * Feedback session deletion — ADR-192 as superseded by ADR-193.
 *
 * Any session can be deleted (any status, with or without individuals) by a
 * viewer who may delete sessions in its cluster. radar-api decides that and
 * sends `canDelete` on every session of the list. Before deleting, the dialog
 * loads GET /feedback-sessions/:id/deletion-preview, shows what will be
 * destroyed, and sends the counts it showed back as `confirmedCounts` together
 * with a mandatory reason. The API refuses (409 DELETION_PREVIEW_STALE) when the
 * session changed in between.
 */

export const DELETION_PREVIEW_STALE_CODE = 'DELETION_PREVIEW_STALE';

// Must match CONFIRMED_COUNT_KEYS in radar-api's feedbackSessionDeletion.service.
export const CONFIRMED_COUNT_KEYS = ['assignmentCount', 'responseCount', 'discardedFoDCount'];

/** Show the card's Delete action only on an explicit server `true`. */
export const canDeleteSession = (session) => session?.canDelete === true;

export const DELETE_SESSION_TITLE = 'Delete feedback session';

/** The counts to send back with the delete: exactly what the preview showed. */
export const getConfirmedCounts = (preview) =>
  Object.fromEntries(CONFIRMED_COUNT_KEYS.map((key) => [key, preview?.[key] ?? 0]));

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

/**
 * The lines the dialog lists under "This will permanently delete:". Zero
 * counts are left out; an empty session gets a single reassuring line.
 */
export const getDeletionImpactLines = (preview) => {
  if (!preview) {
    return [];
  }
  if (!preview.assignmentCount) {
    return ['No individuals are attached, so no feedback will be deleted.'];
  }
  const lines = [plural(preview.assignmentCount, 'individual and their assignment', 'individuals and their assignments')];
  if (preview.completedAssignmentCount) {
    lines.push(`${plural(preview.completedAssignmentCount, 'completed assignment', 'completed assignments')} (results disappear from reports)`);
  }
  if (preview.reviewerCount) {
    lines.push(plural(preview.reviewerCount, 'reviewer assignment', 'reviewer assignments'));
  }
  if (preview.responseCount) {
    lines.push(plural(preview.responseCount, 'response', 'responses'));
  }
  if (preview.discardedFoDCount) {
    lines.push(plural(preview.discardedFoDCount, 'unsubmitted Start/Stop/Continue', 'unsubmitted Start/Stop/Continue'));
  }
  return lines;
};
